<template>
    <div @mouseenter="prefetchMenu"
         @contextmenu.prevent="handleContextMenu"
         :class="wrapperClass"
         :style="wrapperStyle">
        <slot></slot>

        <div v-if="isOpen"
             ref="menuRef"
             class="fixed min-w-60 z-50 bg-white shadow-md rounded p-1 space-y-0.5 context-menu"
             :style="menuStyle">

            <div v-if="loading" class="p-3 text-center text-gray-500">
                <span class="lnr lnr-sync animate-spin"></span>
            </div>

            <div v-else-if="error" class="p-3 text-center text-red-500 text-xs">
                Failed to load menu
            </div>

            <template v-else-if="items.length > 0" v-for="(item, index) in items" :key="index">

                <div v-if="item.separator" class="border-t my-1"></div>

                <div v-else
                     class="relative menu-item"
                     :ref="el => setItemRef(el, index)"
                     @mouseenter="item.children && !item.disabled && onParentEnter(index)"
                     @mouseleave="item.children && !item.disabled && handleParentLeave(index)">

                    <div @click="(event) => !item.disabled && handleClick(item, index, event)"
                         class="w-full flex items-center justify-between gap-x-3 py-1.5 px-3 rounded-lg text-[13px] text-gray-700 hover:bg-gray-100 cursor-pointer icon-clip"
                         :class="{ 'opacity-50 !cursor-not-allowed !hover:bg-white': item.disabled }">

                        <div class="flex gap-3">
                            <span v-if="item.icon" :class="`lnr ${item.icon} !text-base !text-gray-700`"></span>
                            <p class="m-0 p-0">{{ item.label }}</p>
                        </div>

                        <span v-if="item.children && !item.disabled">
                            <svg class="text-gray-700" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M9 6l6 6l-6 6"/>
                            </svg>
                        </span>
                    </div>

                    <div v-if="item.children && !item.disabled"
                         v-show="openSubmenus.has(index)"
                         :ref="el => setSubmenuRef(el, index)"
                         @mouseenter="handleSubmenuEnter(index)"
                         @mouseleave="handleSubmenuLeave(index)"
                         class="submenu absolute -top-[4px] left-full min-w-60 bg-white shadow-md rounded p-1 space-y-0.5 ml-2">

                        <template v-for="(child, childIndex) in item.children" :key="childIndex">

                            <div v-if="child.separator" class="border-t my-1"></div>

                            <div v-else
                                 @click="(event) => !child.disabled && handleClick(child, childIndex, event)"
                                 :class="{ 'opacity-50 !cursor-not-allowed !hover:bg-white': child.disabled }"
                                 class="w-full flex items-center gap-x-3 py-1.5 px-3 rounded-lg text-[13px] text-gray-700 hover:bg-gray-100 cursor-pointer">

                                <transition name="fade">
                                    <div v-if="copiedIndex === childIndex"
                                         class="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full bg-gray-50 shadow-md p-1 px-2 rounded mb-2">
                                        Copied
                                    </div>
                                </transition>

                                <span v-if="child.icon" :class="`lnr ${child.icon} !text-base !text-gray-700`"></span>
                                <p class="m-0 p-0">{{ child.label }}</p>
                            </div>

                        </template>
                    </div>

                </div>

            </template>
        </div>
    </div>
</template>

<script setup>
import {ref, computed, watch, nextTick} from 'vue';
import {useContextMenu} from './Composable/useContextMenu.js';
import {useCompany} from '@/composables/useCompany.js';

const props = defineProps({
    resource: {
        type: String,
        required: true
    },
    resourceId: {
        type: [Number, String],
        required: true
    },
    wrapperClass: {
        type: String,
        default: 'context-menu-wrapper'
    },
    wrapperStyle: {
        type: [String, Object],
        default: null
    }
});

const {buildCompanyApiInternalRoute} = useCompany();

const {
    isOpen,
    position,
    copiedIndex,
    openMenu,
    itemClicked,
    openSubmenus,
    handleParentEnter,
    handleParentLeave,
    handleSubmenuEnter,
    handleSubmenuLeave,
    setItemRef,
    setSubmenuRef,
    getItemRef,
    getSubmenuRef
} = useContextMenu();

const menuRef = ref(null);
const loading = ref(false);
const error = ref(false);
const items = ref([]);
const hasPrefetched = ref(false);

const fetchUrl = computed(() => {
    return buildCompanyApiInternalRoute(`${props.resource}/${props.resourceId}/context-menu`);
});

const VIEWPORT_PADDING = 10;

const prefetchMenu = async () => {
    if (hasPrefetched.value || loading.value) return;

    hasPrefetched.value = true;
    loading.value = true;

    try {
        const response = await fetch(fetchUrl.value);

        if (!response.ok) {
            throw new Error('Failed to fetch menu');
        }

        const data = await response.json();
        items.value = data.items || data;
        error.value = false;
    } catch (e) {
        console.error('Error fetching context menu:', e);
        error.value = true;
        items.value = [];
    } finally {
        loading.value = false;
    }
};

const handleContextMenu = async (event) => {
    if (hasPrefetched.value && items.value.length > 0) {
        openMenu(event);
        return;
    }

    openMenu(event);

    if (!loading.value && !hasPrefetched.value) {
        await prefetchMenu();
    }
};

const menuStyle = computed(() => {
    if (!menuRef.value) {
        return {
            top: `${position.value.y}px`,
            left: `${position.value.x}px`
        };
    }

    const menuRect = menuRef.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.value.x;
    let y = position.value.y;

    if (x + menuRect.width > viewportWidth) {
        x = viewportWidth - menuRect.width - VIEWPORT_PADDING;
    }
    if (y + menuRect.height > viewportHeight) {
        y = viewportHeight - menuRect.height - VIEWPORT_PADDING;
    }
    if (x < VIEWPORT_PADDING) {
        x = VIEWPORT_PADDING;
    }
    if (y < VIEWPORT_PADDING) {
        y = VIEWPORT_PADDING;
    }

    return {
        top: `${y}px`,
        left: `${x}px`
    };
});

watch(isOpen, async (isMenuOpen) => {
    if (isMenuOpen) {
        await nextTick();
        menuStyle.value;
    }
});

const positionSubmenu = (index) => {
    const submenu = getSubmenuRef(index);
    const parentItem = getItemRef(index);

    if (!submenu || !parentItem) return;

    Object.assign(submenu.style, {
        left: '100%',
        right: 'auto',
        marginLeft: '0.5rem',
        marginRight: '0',
        top: '-4px'
    });

    nextTick(() => {
        const submenuRect = submenu.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (submenuRect.right > viewportWidth - VIEWPORT_PADDING) {
            Object.assign(submenu.style, {
                left: 'auto',
                right: '100%',
                marginLeft: '0',
                marginRight: '0.5rem'
            });
        }

        nextTick(() => {
            const updatedRect = submenu.getBoundingClientRect();

            if (updatedRect.bottom > viewportHeight - VIEWPORT_PADDING) {
                const overflow = updatedRect.bottom - (viewportHeight - VIEWPORT_PADDING);
                submenu.style.top = `${-4 - overflow}px`;
            }

            const finalRect = submenu.getBoundingClientRect();
            if (finalRect.top < VIEWPORT_PADDING) {
                const adjustment = VIEWPORT_PADDING - finalRect.top;
                const currentTop = parseInt(submenu.style.top) || -4;
                submenu.style.top = `${currentTop + adjustment}px`;
            }
        });
    });
};

const handleClick = (item, index, event) => itemClicked(item, index, event);
const onParentEnter = (index) => handleParentEnter(index, positionSubmenu);
</script>

<style scoped>
.context-menu-wrapper {
    display: inline-block;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.12s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style>